import React, { useRef } from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import Helmet from "../components/Helmet/Helmet";
import heroImage from "../assets/all-images/bg12.jpg";
import founderImage from "../assets/ROha1.jpg";
import workshopImage from "../assets/mining.jpg";
import gemstoneImage from "../assets/all-images/bg11.jpg";
import craftImage from "../assets/ring.jpg";
import { siteConfig } from "../utils/site";
import "../styles/about-luxury.css";

/* Animation Variants */
const fadeUp = { 
  hidden: { opacity: 0, y: 60 }, 
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } } 
};

const fadeIn = { 
  hidden: { opacity: 0 }, 
  visible: { opacity: 1, transition: { duration: 1, ease: "easeOut" } } 
};

const scaleIn = { 
  hidden: { opacity: 0, scale: 0.95 }, 
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } } 
};

const stagger = { 
  visible: { transition: { staggerChildren: 0.15 } } 
};

const Reveal = ({ children, variants = fadeUp, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const v = { ...variants, visible: { ...variants.visible, transition: { ...variants.visible.transition, delay } } };
  return <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={v}>{children}</motion.div>;
};

const RevealGroup = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const v = { ...stagger, visible: { ...stagger.visible, transition: { staggerChildren: 0.15, delayChildren: delay } } };
  return <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={v}>{children}</motion.div>;
};

const timeline = [
  { 
    year: "1994", 
    title: "A Legacy Begins", 
    text: "In the heart of Addis Ababa, Roha Jewellery was born from a profound reverence for Ethiopian gemstones and the ancient art of goldsmithing passed down through generations.",
    icon: "◈"
  },
  { 
    year: "2005", 
    title: "Trust Through Craft", 
    text: "Word spread quietly through the city. Customers returned not for marketing, but for the unmistakable quality of hand-selected stones and meticulous craftsmanship that honored their stories.",
    icon: "◇"
  },
  { 
    year: "2018", 
    title: "Beyond Borders", 
    text: "Ethiopian opals began capturing global attention. Roha embraced the digital age, sharing the fire of Wollo opals and the depth of Tigray emeralds with collectors worldwide.",
    icon: "◉"
  },
  { 
    year: "2026", 
    title: "A New Chapter", 
    text: "Today, Roha stands as Ethiopia's premier luxury jewellery house—where ancient heritage meets contemporary elegance, and every piece tells a story written in stone and gold.",
    icon: "✦"
  },
];

const values = [
  { 
    icon: "◈", 
    title: "Ethiopian Heritage", 
    text: "Every gemstone carries the soul of Ethiopian earth—from the highlands of Tigray to the opal fields of Wollo. We honor this legacy in every piece we create." 
  },
  { 
    icon: "◇", 
    title: "Master Craftsmanship", 
    text: "Our artisans blend centuries-old Habesha goldsmithing techniques with modern precision, creating jewellery that transcends time and trend." 
  },
  { 
    icon: "◉", 
    title: "Ethical Sourcing", 
    text: "We trace every stone to its origin, ensuring ethical mining practices and authentic provenance. Your jewellery carries not just beauty, but integrity." 
  },
  { 
    icon: "✦", 
    title: "Timeless Design", 
    text: "Fashion fades. True luxury endures. We create pieces that become family heirlooms, carrying memories and meaning across generations." 
  },
  { 
    icon: "◈", 
    title: "Bespoke Excellence", 
    text: "Your story deserves a unique expression. Our designers work intimately with you to create one-of-a-kind pieces that reflect your personal journey." 
  },
  { 
    icon: "◇", 
    title: "Global Trust", 
    text: "From Addis Ababa to London, Tokyo to New York—our clients trust us with their most precious moments. We deliver that trust with every shipment." 
  },
];

const craftSteps = [
  { icon: "◈", title: "Stone Selection", text: "Each gemstone is hand-selected by our master gemologists for exceptional color, clarity, and character." },
  { icon: "◇", title: "Design Creation", text: "Our designers sketch concepts that honor both the stone's natural beauty and your personal vision." },
  { icon: "◉", title: "Artisan Crafting", text: "Skilled hands shape precious metals using techniques passed down through generations of Ethiopian goldsmiths." },
  { icon: "✦", title: "Final Perfection", text: "Every detail is refined, every surface polished, until the piece achieves the perfection it deserves." },
];

const AboutLuxury = () => {
  return (
    <>
      <Helmet>
        <title>About Roha Jewellery | Ethiopian Luxury Jewellery Heritage</title>
        <meta
          name="description"
          content="Discover the story of Roha Jewellery—Ethiopia's premier luxury jewellery house. Three decades of master craftsmanship, ethical Ethiopian gemstones, and timeless design."
        />
        <link rel="canonical" href={`${siteConfig.url}/about`} />
      </Helmet>

      <main className="about-luxury-page">
        
        {/* ═══════════════════════════════════════════════════════════
            SECTION 1: CINEMATIC HERO
            ═══════════════════════════════════════════════════════════ */}
        <section className="about-hero">
          <div className="about-hero__bg">
            <img src={heroImage} alt="" loading="eager" />
            <div className="about-hero__overlay"></div>
            <div className="about-hero__overlay-gradient"></div>
          </div>

          {/* Floating Particles */}
          <div className="about-hero__particles">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className="about-hero__particle"></div>
            ))}
          </div>

          <Container>
            <motion.div 
              className="about-hero__content"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <motion.div className="about-hero__eyebrow" variants={fadeUp}>
                <span className="about-hero__eyebrow-line"></span>
                <span>Our Story</span>
                <span className="about-hero__eyebrow-line"></span>
              </motion.div>

              <motion.h1 className="about-hero__title" variants={fadeUp}>
                Jewellery Rooted in<br />
                <span className="about-hero__title-accent">Ethiopian Earth</span>,<br />
                Crafted for Timeless Elegance
              </motion.h1>

              <motion.p className="about-hero__subtitle" variants={fadeUp}>
                For over three decades, Roha Jewellery has been Ethiopia's guardian of gemstone artistry—
                where ancient Habesha traditions meet contemporary luxury, and every piece carries the 
                fire of Ethiopian opals, the depth of Tigray emeralds, and the soul of master craftsmen.
              </motion.p>

              <motion.div className="about-hero__actions" variants={fadeUp}>
                <Link to="/products" className="about-btn about-btn--primary">
                  <span>Explore Our Collection</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
                <Link to="/design-request" className="about-btn about-btn--secondary">
                  <span>Commission Bespoke Piece</span>
                </Link>
              </motion.div>

              <motion.div className="about-hero__stats" variants={fadeUp}>
                <div className="about-hero__stat">
                  <span className="about-hero__stat-value">30+</span>
                  <span className="about-hero__stat-label">Years of Heritage</span>
                </div>
                <div className="about-hero__stat">
                  <span className="about-hero__stat-value">500+</span>
                  <span className="about-hero__stat-label">Unique Designs</span>
                </div>
                <div className="about-hero__stat">
                  <span className="about-hero__stat-value">50+</span>
                  <span className="about-hero__stat-label">Countries Served</span>
                </div>
              </motion.div>
            </motion.div>
          </Container>

          {/* Scroll Indicator */}
          <div className="about-hero__scroll">
            <span>Discover Our Journey</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 5V19M12 19L5 12M12 19L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 2: BRAND STORY - EDITORIAL LAYOUT
            ═══════════════════════════════════════════════════════════ */}
        <section className="about-story">
          <Container>
            <div className="about-story__grid">
              <Reveal variants={scaleIn}>
                <div className="about-story__image-main">
                  <img src={founderImage} alt="Roha Jewellery Heritage" loading="lazy" />
                  <div className="about-story__image-glow"></div>
                </div>
              </Reveal>

              <div className="about-story__content">
                <Reveal>
                  <p className="about-eyebrow">
                    <span className="about-eyebrow__icon">◈</span>
                    The Roha Legacy
                  </p>
                </Reveal>

                <Reveal delay={0.1}>
                  <h2 className="about-story__heading">
                    Where Ancient Craft<br />Meets Modern Luxury
                  </h2>
                </Reveal>

                <Reveal delay={0.2}>
                  <div className="about-story__text">
                    <p>
                      In 1994, in a modest workshop in Addis Ababa, Roha Jewellery was born from a simple 
                      but profound belief: that Ethiopian gemstones deserved to be celebrated with the same 
                      reverence as the world's most prestigious jewellery houses.
                    </p>
                    <p>
                      What began as a family tradition of goldsmithing has evolved into Ethiopia's premier 
                      luxury jewellery brand. Yet our essence remains unchanged—an unwavering commitment to 
                      the stones that emerge from Ethiopian earth, the hands that shape them, and the stories 
                      they carry.
                    </p>
                    <p>
                      Today, Roha stands at the intersection of heritage and innovation. Our master artisans 
                      still employ centuries-old Habesha techniques, but with a contemporary vision that 
                      resonates with collectors from London to Tokyo, New York to Dubai.
                    </p>
                  </div>
                </Reveal>

                <Reveal delay={0.3}>
                  <div className="about-story__features">
                    <div className="about-story__feature">
                      <span className="about-story__feature-icon">◇</span>
                      <div>
                        <h4>Addis Ababa Showroom</h4>
                        <p>{siteConfig.address}</p>
                      </div>
                    </div>
                    <div className="about-story__feature">
                      <span className="about-story__feature-icon">◉</span>
                      <div>
                        <h4>Global Delivery</h4>
                        <p>Secure worldwide shipping with full insurance and tracking</p>
                      </div>
                    </div>
                    <div className="about-story__feature">
                      <span className="about-story__feature-icon">✦</span>
                      <div>
                        <h4>Certified Authenticity</h4>
                        <p>Every piece comes with full gemological certification</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </Container>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 3: IMMERSIVE GEMSTONE EXPERIENCE
            ═══════════════════════════════════════════════════════════ */}
        <section className="about-gemstone">
          <div className="about-gemstone__bg">
            <img src={gemstoneImage} alt="" loading="lazy" />
            <div className="about-gemstone__overlay"></div>
          </div>

          <Container>
            <Reveal>
              <div className="about-gemstone__content">
                <p className="about-eyebrow about-eyebrow--light">
                  <span className="about-eyebrow__icon">◈</span>
                  Ethiopian Gemstone Heritage
                </p>
                <h2>The Fire of Wollo,<br />The Depth of Tigray</h2>
                <p>
                  Ethiopia's geological treasures are among the world's most extraordinary. From the 
                  legendary play-of-color in Wollo opals to the vivid green of Tigray emeralds, from 
                  the deep red of highland rubies to the royal blue of Gambela sapphires—each stone 
                  carries millions of years of African earth's story.
                </p>
                <p>
                  We don't just source gemstones. We curate them. Every stone that enters our workshop 
                  has been personally inspected, its origin traced, its character understood. This is 
                  the Roha promise: authenticity, provenance, and beauty that transcends mere aesthetics.
                </p>
                <Link to="/products" className="about-btn about-btn--glass">
                  <span>Discover Ethiopian Gemstones</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
            </Reveal>
          </Container>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 4: ROHA MINING - FULL WIDTH BANNER
            ═══════════════════════════════════════════════════════════ */}
        <section className="about-mining">
          <div className="about-mining__bg">
            <img src={workshopImage} alt="Roha Mining Operations" loading="lazy" />
            <div className="about-mining__overlay"></div>
            <div className="about-mining__overlay-gradient"></div>
          </div>

          {/* Floating Mining Particles */}
          <div className="about-mining__particles">
            {Array.from({ length: 15 }).map((_, i) => (
              <div key={i} className="about-mining__particle"></div>
            ))}
          </div>

          <Container>
            <Reveal>
              <div className="about-mining__content">
                <div className="about-mining__badge">
                  <span className="about-mining__badge-icon">◈</span>
                  <span>From Earth to Elegance</span>
                </div>

                <h2 className="about-mining__title">
                  Our Own Gold &<br />
                  <span className="about-mining__title-accent">Gemstone Mines</span><br />
                  in Ethiopia
                </h2>

                <p className="about-mining__subtitle">
                  Roha Jewellery owns and operates ethical mining sites across Ethiopia's most 
                  gemstone-rich regions. From the opal fields of Wollo to the gold deposits of 
                  the highlands, we control every step of the journey—from earth to your hands.
                </p>

                <div className="about-mining__features">
                  <div className="about-mining__feature">
                    <div className="about-mining__feature-icon">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <path d="M20 5L5 15L20 25L35 15L20 5Z" fill="currentColor" opacity="0.3"/>
                        <path d="M5 15L20 35L35 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M20 25L20 35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div>
                      <h4>Ethiopian Opal Mines</h4>
                      <p>Wollo & Shewa regions producing world-renowned fire opals</p>
                    </div>
                  </div>

                  <div className="about-mining__feature">
                    <div className="about-mining__feature-icon">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <circle cx="20" cy="20" r="12" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <circle cx="20" cy="20" r="6" fill="currentColor" opacity="0.3"/>
                        <path d="M20 8V12M20 28V32M8 20H12M28 20H32" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div>
                      <h4>Gold Mining Operations</h4>
                      <p>Highland gold deposits refined to 22K and 24K purity</p>
                    </div>
                  </div>

                  <div className="about-mining__feature">
                    <div className="about-mining__feature-icon">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <rect x="8" y="12" width="24" height="20" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <path d="M12 12V8C12 6.89543 12.8954 6 14 6H26C27.1046 6 28 6.89543 28 8V12" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="20" cy="22" r="3" fill="currentColor" opacity="0.3"/>
                      </svg>
                    </div>
                    <div>
                      <h4>Emerald & Precious Stones</h4>
                      <p>Tigray emeralds, rubies, and sapphires from our certified sites</p>
                    </div>
                  </div>
                </div>

                <div className="about-mining__stats">
                  <div className="about-mining__stat">
                    <span className="about-mining__stat-value">5+</span>
                    <span className="about-mining__stat-label">Mining Sites</span>
                  </div>
                  <div className="about-mining__stat">
                    <span className="about-mining__stat-value">100%</span>
                    <span className="about-mining__stat-label">Ethical Sourcing</span>
                  </div>
                  <div className="about-mining__stat">
                    <span className="about-mining__stat-value">200+</span>
                    <span className="about-mining__stat-label">Local Miners</span>
                  </div>
                </div>

                <div className="about-mining__actions">
                  <Link to="/products" className="about-btn about-btn--primary">
                    <span>Shop Mine-to-Market Collection</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                  <Link to="/contact" className="about-btn about-btn--glass">
                    <span>Visit Our Mining Sites</span>
                  </Link>
                </div>

                <div className="about-mining__certifications">
                  <div className="about-mining__cert">
                    <span className="about-mining__cert-icon">✓</span>
                    <span>Fair Trade Certified</span>
                  </div>
                  <div className="about-mining__cert">
                    <span className="about-mining__cert-icon">✓</span>
                    <span>Ethical Mining Practices</span>
                  </div>
                  <div className="about-mining__cert">
                    <span className="about-mining__cert-icon">✓</span>
                    <span>Environmental Responsibility</span>
                  </div>
                  <div className="about-mining__cert">
                    <span className="about-mining__cert-icon">✓</span>
                    <span>Community Development</span>
                  </div>
                </div>
              </div>
            </Reveal>
          </Container>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 5: CERTIFICATIONS
            ═══════════════════════════════════════════════════════════ */}
        <section className="about-certificates-section">
          <Container>
            <Reveal>
              <div className="about-section-header about-section-header--center">
                <p className="about-eyebrow">
                  <span className="about-eyebrow__icon">✦</span>
                  Official Certifications
                </p>
                <h2>Internationally Recognized<br />Quality & Ethics</h2>
                <p className="about-section-subtitle">
                  Our commitment to excellence is validated by leading international certification bodies.
                </p>
              </div>
            </Reveal>

            <RevealGroup>
              <div className="about-certificates-grid">
                <motion.div className="about-certificate-card" variants={fadeUp}>
                  <div className="about-certificate-card__icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <path d="M24 4L30.18 14.52L42 16.54L33.9 25.46L35.64 37.46L24 32L12.36 37.46L14.1 25.46L6 16.54L17.82 14.52L24 4Z" fill="currentColor" fillOpacity="0.1"/>
                      <path d="M24 4L30.18 14.52L42 16.54L33.9 25.46L35.64 37.46L24 32L12.36 37.46L14.1 25.46L6 16.54L17.82 14.52L24 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M24 20L27 23L30 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="about-certificate-card__content">
                    <h3>International Gemological Institute (IGI)</h3>
                    <p>Full gemstone certification for authenticity, quality, and origin verification. Each Roha piece comes with IGI certification detailing carat weight, color, clarity, and cut.</p>
                    <div className="about-certificate-card__badges">
                      <span className="about-certificate-card__badge">Diamond Grading</span>
                      <span className="about-certificate-card__badge">Gemstone Origin</span>
                      <span className="about-certificate-card__badge">Quality Assurance</span>
                    </div>
                  </div>
                  <div className="about-certificate-card__footer">
                    <span>Certificate #: IGI-ET-2024-001</span>
                    <span>Valid: 2024-2026</span>
                  </div>
                </motion.div>

                <motion.div className="about-certificate-card" variants={fadeUp}>
                  <div className="about-certificate-card__icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" fill="none"/>
                      <path d="M24 12V24L32 28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 20L20 24L24 20L28 24L32 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="about-certificate-card__content">
                    <h3>Ethical Mining Council (EMC)</h3>
                    <p>Certification for sustainable and ethical mining practices. Ensures fair labor conditions, environmental protection, and community development in all Roha mining operations.</p>
                    <div className="about-certificate-card__badges">
                      <span className="about-certificate-card__badge">Fair Trade</span>
                      <span className="about-certificate-card__badge">Environmental</span>
                      <span className="about-certificate-card__badge">Community</span>
                    </div>
                  </div>
                  <div className="about-certificate-card__footer">
                    <span>Certificate #: EMC-ET-2024-002</span>
                    <span>Valid: 2024-2025</span>
                  </div>
                </motion.div>
              </div>
            </RevealGroup>
          </Container>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 6: LUXURY TIMELINE
            ═══════════════════════════════════════════════════════════ */}
        <section className="about-timeline-section">
          <Container>
            <Reveal>
              <div className="about-section-header">
                <p className="about-eyebrow">
                  <span className="about-eyebrow__icon">◈</span>
                  Our Journey
                </p>
                <h2>Three Decades of<br />Ethiopian Luxury</h2>
                <p className="about-section-subtitle">
                  A legacy built stone by stone, piece by piece, story by story.
                </p>
              </div>
            </Reveal>

            <div className="about-timeline">
              <div className="about-timeline__line"></div>
              
              <RevealGroup>
                {timeline.map((item, index) => (
                  <motion.div 
                    key={item.year} 
                    className={`about-timeline__item ${index % 2 === 0 ? 'about-timeline__item--left' : 'about-timeline__item--right'}`}
                    variants={fadeUp}
                  >
                    <div className="about-timeline__marker">
                      <span className="about-timeline__icon">{item.icon}</span>
                      <div className="about-timeline__glow"></div>
                    </div>
                    <div className="about-timeline__card">
                      <span className="about-timeline__year">{item.year}</span>
                      <h3 className="about-timeline__title">{item.title}</h3>
                      <p className="about-timeline__text">{item.text}</p>
                    </div>
                  </motion.div>
                ))}
              </RevealGroup>
            </div>
          </Container>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 5: VALUES - PREMIUM CARDS
            ═══════════════════════════════════════════════════════════ */}
        <section className="about-values">
          <Container>
            <Reveal>
              <div className="about-section-header">
                <p className="about-eyebrow">
                  <span className="about-eyebrow__icon">◈</span>
                  What Defines Us
                </p>
                <h2>The Pillars of<br />Roha Excellence</h2>
              </div>
            </Reveal>

            <RevealGroup>
              <motion.div className="about-values__grid" variants={stagger}>
                {values.map((value) => (
                  <motion.div key={value.title} className="about-value-card" variants={fadeUp}>
                    <div className="about-value-card__glow"></div>
                    <span className="about-value-card__icon">{value.icon}</span>
                    <h3 className="about-value-card__title">{value.title}</h3>
                    <p className="about-value-card__text">{value.text}</p>
                  </motion.div>
                ))}
              </motion.div>
            </RevealGroup>
          </Container>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 6: THE ART BEHIND EVERY STONE
            ═══════════════════════════════════════════════════════════ */}
        <section className="about-craft">
          <Container>
            <div className="about-craft__grid">
              <div className="about-craft__content">
                <Reveal>
                  <p className="about-eyebrow">
                    <span className="about-eyebrow__icon">◈</span>
                    Master Craftsmanship
                  </p>
                </Reveal>

                <Reveal delay={0.1}>
                  <h2 className="about-craft__heading">
                    The Art Behind<br />Every Stone
                  </h2>
                </Reveal>

                <Reveal delay={0.2}>
                  <p className="about-craft__text">
                    Creating a Roha piece is not manufacturing—it is artistry. From the moment a rough 
                    stone arrives at our workshop to the final polish that reveals its inner fire, every 
                    step is guided by hands that have spent decades perfecting their craft.
                  </p>
                </Reveal>

                <RevealGroup delay={0.3}>
                  <motion.div className="about-craft__steps" variants={stagger}>
                    {craftSteps.map((step) => (
                      <motion.div key={step.title} className="about-craft__step" variants={fadeUp}>
                        <span className="about-craft__step-icon">{step.icon}</span>
                        <div>
                          <h4>{step.title}</h4>
                          <p>{step.text}</p>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </RevealGroup>

                <Reveal delay={0.4}>
                  <Link to="/designs" className="about-btn about-btn--primary">
                    <span>View Our Designs</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                </Reveal>
              </div>

              <Reveal variants={scaleIn} delay={0.2}>
                <div className="about-craft__images">
                  <div className="about-craft__image-main">
                    <img src={craftImage} alt="Roha Jewellery Craftsmanship" loading="lazy" />
                    <div className="about-craft__image-glow"></div>
                  </div>
                  <div className="about-craft__image-accent">
                    <img src={workshopImage} alt="Artisan Workshop" loading="lazy" />
                  </div>
                </div>
              </Reveal>
            </div>
          </Container>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 7: LUXURY CTA
            ═══════════════════════════════════════════════════════════ */}
        <section className="about-cta">
          <Container>
            <Reveal>
              <div className="about-cta__card">
                <div className="about-cta__glow"></div>
                <div className="about-cta__border"></div>
                
                <div className="about-cta__content">
                  <span className="about-cta__icon">◈</span>
                  <h2>Begin Your Roha Journey</h2>
                  <p>
                    Whether you seek a signature piece from our collection or wish to create something 
                    entirely unique, our team is ready to guide you through an experience as exceptional 
                    as the jewellery itself.
                  </p>

                  <div className="about-cta__actions">
                    <Link to="/contact" className="about-btn about-btn--primary about-btn--lg">
                      <span>Book Private Consultation</span>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Link>
                    <Link to="/design-request" className="about-btn about-btn--secondary about-btn--lg">
                      <span>Commission Bespoke Piece</span>
                    </Link>
                  </div>

                  <div className="about-cta__info">
                    <div className="about-cta__info-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10 2C6.13 2 3 5.13 3 9c0 5.25 7 11 7 11s7-5.75 7-11c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="10" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                      <span>{siteConfig.address}</span>
                    </div>
                    <div className="about-cta__info-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2 3h16M2 7h16M2 11h16M2 15h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      <span>{siteConfig.phones[0]}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </Container>
        </section>

      </main>
    </>
  );
};

export default AboutLuxury;
